import {
  createMessageChannel,
  createWindowMessageReader,
  createWindowMessageWriter,
  initializeSDK,
  type MessageChannel,
  type NetlifySDK,
  type UIExtensionHostEvent,
  UIExtensionHostEventSchema,
  type UIExtensionSurfaceEvent,
  withReaderTransform,
} from "@netlify/sdk--ui-core";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { Card } from "./components/vendor/Card/main.js";
import { ListLoader } from "./components/vendor/ListLoader/ListLoader.jsx";

const DefaultLoader = () => (
  <Card>
    <ListLoader />
  </Card>
);

export const NetlifySDKContext = createContext<{
  _updateMarker: number;
  sdk: NetlifySDK | null;
}>({
  _updateMarker: 0,
  sdk: null,
});

export interface NetlifySDKProviderProps {
  children: React.ReactNode;
  loader?: React.ReactNode | (() => React.JSX.Element) | undefined;
  messageChannel?:
    | MessageChannel<UIExtensionHostEvent, UIExtensionSurfaceEvent>
    | undefined;
  sdk?: NetlifySDK | undefined;
}

export const NetlifySDKProvider = ({
  children,
  loader: Loader = DefaultLoader,
  messageChannel: initialMessageChannel,
  sdk: initialSDK,
}: NetlifySDKProviderProps): React.JSX.Element => {
  const [sdk, setSDK] = useState<NetlifySDK | null>(initialSDK ?? null);
  const [updateMarker, setUpdateMarker] = useState<number>(0);

  const [abortController] = useState(new AbortController());
  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, [abortController]);

  const messageChannel = useMemo<
    MessageChannel<UIExtensionHostEvent, UIExtensionSurfaceEvent>
  >(
    () =>
      initialMessageChannel ??
      createMessageChannel(
        withReaderTransform(
          createWindowMessageReader({
            signal: abortController.signal,
            // TODO(ndhoule): Set these origins depending on the environment
            isOriginPermitted: (origin: string) => {
              // Allow from self (always safe, and useful in particular in testing environments)
              if (origin === window.location.origin) {
                return true;
              }

              let parsedOrigin: URL;
              try {
                parsedOrigin = new URL(origin);
              } catch {
                return false;
              }

              // Allow from the production Netlify app (and deploy previews)
              if (
                parsedOrigin.host.replace(/^deploy-preview-\d+--/, "") ===
                "app.netlify.com"
              ) {
                return true;
              }

              // Allow from the staging Netlify app (and deploy previews)
              if (
                parsedOrigin.host.replace(/^deploy-preview-\d+--/, "") ===
                "app.netlifystg.com"
              ) {
                return true;
              }

              // Allow from development Netlify app
              if (parsedOrigin.host === "localhost:8888") {
                return true;
              }

              return false;
            },
          }),
          (message) => {
            const result = UIExtensionHostEventSchema.safeParse(message);
            return result.success ? result.data : null;
          },
        ),
        createWindowMessageWriter({
          signal: abortController.signal,
          // TODO(ndhoule): Set the target origin depending on the environment
          targetOrigin: "*",
          targetWindow: window.parent,
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abortController.signal],
  );

  useEffect(() => {
    if (sdk === null) {
      // TODO(ndhoule): Handle explicit initialization errors; if we fail to initialize the SDK we
      // should store that error and display an error screen.
      // TODO(ndhoule): To handle cases where the host isn't responding properly, we should time this
      // operation out (after ~5s?) and display an error. (Not sure yet if we should put the timeout
      // here or in the initializeSDK method.)
      void (async () => {
        setSDK(await initializeSDK({ messageChannel }));
      })();
    }
  }, [messageChannel, sdk, setSDK]);

  // Observe the SDK for changes and update a marker value. (This marker value forces context
  // consumers to calculate a re-render.)
  useEffect(() => {
    if (sdk === null) {
      return;
    }

    return sdk.onChange(() => {
      setUpdateMarker((marker) =>
        marker === Number.MAX_SAFE_INTEGER ? 0 : marker + 1,
      );
    });
  }, [sdk]);

  if (sdk === null) {
    if (typeof Loader === "function") {
      return <Loader />;
    }
    // @ts-expect-error FIXME(ndhoule)
    return Loader;
  }

  return (
    <NetlifySDKContext.Provider value={{ sdk, _updateMarker: updateMarker }}>
      {children}
    </NetlifySDKContext.Provider>
  );
};
